/**
 * データセット名変更時の依存SQLクエリ更新ロジック
 *
 * データセット名が変更されたとき、そのデータセットを参照する
 * SQL Query Operationのクエリ文を更新する。
 */

import type { DataSet, DerivedDataSet, SQLQueryOperation } from '../../../types/dataset.types';
import type { TableReference } from '../duckdb';
import { replaceTableNamesAtLocations, sqlReferencesTable } from './replaceTableName';
import logger from '../logger';

/**
 * テーブル参照を抽出できるDuckDBインターフェース
 */
interface DuckDBTableReferenceExtractor {
  extractTableReferencesWithLocation(query: string): Promise<TableReference[]>;
}

/**
 * データセット名の変更に伴い、依存するSQL Query Operationを更新する
 *
 * @param datasets 全データセット
 * @param oldName 変更前のデータセット名
 * @param newName 変更後のデータセット名
 * @param duckdb DuckDBクエリエンジン
 * @returns 更新後のデータセットマップ
 */
export async function updateDependentSQLQueries(
  datasets: Record<string, DataSet>,
  oldName: string,
  newName: string,
  duckdb: DuckDBTableReferenceExtractor
): Promise<Record<string, DataSet>> {
  const updatedDatasets = { ...datasets };
  let updateCount = 0;

  for (const [id, dataset] of Object.entries(datasets)) {
    // DerivedDataSetのSQL Query Operationのみ対象
    if (dataset.type !== 'derived') continue;

    const derived = dataset as DerivedDataSet;
    if (derived.operation?.type !== 'sql_query') continue;

    const sqlOp = derived.operation as SQLQueryOperation;

    try {
      // ASTから位置情報付きテーブル参照を取得
      const tableRefs = await duckdb.extractTableReferencesWithLocation(sqlOp.query);

      // クエリがoldNameを参照しているか確認
      if (!sqlReferencesTable(tableRefs, oldName)) continue;

      // 位置情報を使ってテーブル名を置換
      const newQuery = replaceTableNamesAtLocations(
        sqlOp.query,
        tableRefs,
        oldName,
        newName
      );

      if (newQuery !== sqlOp.query) {
        logger.info('[updateDependentSQLQueries] Updated SQL query', {
          datasetId: id,
          datasetName: derived.name,
          oldQuery: sqlOp.query,
          newQuery
        });

        updatedDatasets[id] = {
          ...derived,
          operation: {
            ...sqlOp,
            query: newQuery
          },
          // dataキャッシュをクリア（再評価が必要）
          data: undefined
        };

        updateCount++;
      }
    } catch (error) {
      // AST解析に失敗した場合はスキップ（クエリが構文エラーを含む可能性）
      logger.warn('[updateDependentSQLQueries] Failed to update query', {
        datasetId: id,
        datasetName: derived.name,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }

  if (updateCount > 0) {
    logger.info('[updateDependentSQLQueries] Updated dependent queries', {
      oldName,
      newName,
      updateCount
    });
  }

  return updatedDatasets;
}
